// ==== REDBRIDGE:ALL ====
// Feature: Push Notifications Hook
// Reason: Enable push notifications across all apps
// ============================================

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

interface PushStatus {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  // Debug info
  swRegistered: boolean;
  swState: string | null;
  vapidKeyLoaded: boolean;
}

interface NotificationPreferences {
  notifications_enabled: boolean;
  // Feed Mill
  feedmill_chat_messages: boolean;
  feedmill_order_status: boolean;
  feedmill_low_inventory: boolean;
  // Wholesale
  wholesale_order_confirm: boolean;
  wholesale_shipment_updates: boolean;
  wholesale_invoice_ready: boolean;
  // Store Club
  storeclub_order_ready: boolean;
  storeclub_reward_milestone: boolean;
  // Bridge Club
  bridgeclub_new_member: boolean;
  bridgeclub_member_milestone: boolean;
  // Active portals
  active_portals: string[];
}

type PortalType = 'admin' | 'mill' | 'store' | 'wholesale' | 'storeclub' | 'bridgeclub';

// Convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(portal: PortalType = 'admin') {
  const [status, setStatus] = useState<PushStatus>({
    isSupported: false,
    permission: 'unsupported',
    isSubscribed: false,
    isLoading: true,
    error: null,
    swRegistered: false,
    swState: null,
    vapidKeyLoaded: false,
  });
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [vapidKey, setVapidKey] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const addDebug = (msg: string) => {
    console.log(`[Push Debug] ${msg}`);
    setDebugInfo(prev => [...prev.slice(-19), `${new Date().toLocaleTimeString()}: ${msg}`]);
  };

  // Check if push notifications are supported
  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;

  // Initialize and check status
  useEffect(() => {
    const init = async () => {
      addDebug(`Init starting... isSupported=${isSupported}`);

      if (!isSupported) {
        addDebug('Push not supported in this browser');
        setStatus(s => ({ ...s, isSupported: false, isLoading: false }));
        return;
      }

      try {
        // Get permission state
        const permission = Notification.permission;
        addDebug(`Notification.permission = ${permission}`);

        // Check service worker registration
        let swRegistered = false;
        let swState: string | null = null;
        try {
          const reg = await navigator.serviceWorker.getRegistration('/');
          if (reg) {
            swRegistered = true;
            swState = reg.active?.state || reg.installing?.state || reg.waiting?.state || 'unknown';
            addDebug(`Service worker registered, state=${swState}`);
          } else {
            addDebug('No service worker registration found');
          }
        } catch (e) {
          addDebug(`SW check error: ${e}`);
        }

        // Check if already subscribed
        addDebug('Checking subscription status...');
        const statusRes = await api.get('/push/status/', {
          params: { portal }
        });
        addDebug(`Server says subscribed=${statusRes.data.subscribed}, configured=${statusRes.data.configured}`);

        // Get VAPID key
        addDebug('Fetching VAPID key...');
        const vapidRes = await api.get('/push/vapid-key/');
        const vapidKeyLoaded = !!vapidRes.data.publicKey;
        if (vapidRes.data.publicKey) {
          setVapidKey(vapidRes.data.publicKey);
          addDebug(`VAPID key loaded (${vapidRes.data.publicKey.substring(0, 20)}...)`);
        } else {
          addDebug('ERROR: No VAPID public key returned!');
        }

        setStatus({
          isSupported: true,
          permission,
          isSubscribed: statusRes.data.subscribed,
          isLoading: false,
          error: null,
          swRegistered,
          swState,
          vapidKeyLoaded,
        });
        addDebug('Init complete');
      } catch (err) {
        addDebug(`Init error: ${err}`);
        setStatus(s => ({
          ...s,
          isSupported: true,
          permission: Notification.permission,
          isLoading: false,
          error: 'Failed to check notification status',
          swRegistered: false,
          swState: null,
          vapidKeyLoaded: false,
        }));
      }
    };

    init();
  }, [portal, isSupported]);

  // Load notification preferences
  const loadPreferences = useCallback(async () => {
    try {
      const res = await api.get('/push/preferences/');
      setPreferences(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      return null;
    }
  }, []);

  // Update notification preferences
  const updatePreferences = useCallback(async (updates: Partial<NotificationPreferences>) => {
    try {
      await api.patch('/push/preferences/', updates);
      setPreferences(prev => prev ? { ...prev, ...updates } : null);
      return true;
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      return false;
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    addDebug('Subscribe called');

    if (!isSupported) {
      addDebug('ERROR: Push not supported');
      setStatus(s => ({ ...s, error: 'Push notifications not supported' }));
      return false;
    }

    if (!vapidKey) {
      addDebug('ERROR: No VAPID key available');
      setStatus(s => ({ ...s, error: 'VAPID key not loaded' }));
      return false;
    }

    try {
      setStatus(s => ({ ...s, isLoading: true, error: null }));

      // Request permission
      addDebug('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      addDebug(`Permission result: ${permission}`);

      if (permission !== 'granted') {
        setStatus(s => ({
          ...s,
          permission,
          isLoading: false,
          error: 'Notification permission denied',
          swRegistered: s.swRegistered,
          swState: s.swState,
          vapidKeyLoaded: s.vapidKeyLoaded,
        }));
        return false;
      }

      // Register service worker
      addDebug('Registering service worker...');
      // Push lives in the app's one service worker (src/service-worker.ts). Registering
      // a separate push worker at the same scope used to REPLACE the main worker.
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      addDebug(`SW registered, scope: ${registration.scope}`);

      addDebug('Waiting for SW to be ready...');
      await navigator.serviceWorker.ready;
      addDebug('SW is ready');

      // Subscribe to push
      addDebug('Subscribing to PushManager...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      addDebug(`Got subscription, endpoint: ${subscription.endpoint.substring(0, 50)}...`);

      // Send subscription to server
      const subJson = subscription.toJSON();
      addDebug('Sending subscription to server...');
      const res = await api.post('/push/subscribe/', {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys?.p256dh,
          auth: subJson.keys?.auth,
        },
        portal,
      });
      addDebug(`Server response: created=${res.data.created}`);

      setStatus({
        isSupported: true,
        permission: 'granted',
        isSubscribed: true,
        isLoading: false,
        error: null,
        swRegistered: true,
        swState: 'activated',
        vapidKeyLoaded: true,
      });

      addDebug('Subscribe complete!');
      return true;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      addDebug(`Subscribe error: ${errMsg}`);
      console.error('Failed to subscribe to push notifications:', err);
      setStatus(s => ({
        ...s,
        isLoading: false,
        error: `Failed to enable notifications: ${errMsg}`,
        swRegistered: s.swRegistered,
        swState: s.swState,
        vapidKeyLoaded: s.vapidKeyLoaded,
      }));
      return false;
    }
  }, [isSupported, vapidKey, portal]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    try {
      setStatus(s => ({ ...s, isLoading: true, error: null }));

      // Get current subscription
      const registration = await navigator.serviceWorker.getRegistration('/');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          // Unsubscribe locally
          await subscription.unsubscribe();

          // Notify server
          await api.delete('/push/subscribe/', {
            data: {
              endpoint: subscription.endpoint,
              portal,
            }
          });
        }
      }

      setStatus(s => ({
        ...s,
        isSubscribed: false,
        isLoading: false,
        error: null,
      }));

      return true;
    } catch (err) {
      console.error('Failed to unsubscribe from push notifications:', err);
      setStatus(s => ({
        ...s,
        isLoading: false,
        error: 'Failed to disable notifications',
      }));
      return false;
    }
  }, [portal]);

  // Send test notification
  const sendTest = useCallback(async (): Promise<{ success: boolean; result: any }> => {
    addDebug('Sending test notification...');
    try {
      const res = await api.post('/push/test/', { portal });
      addDebug(`Test result: success=${res.data.success}, sent=${res.data.sent}, failed=${res.data.failed}`);
      if (res.data.error) {
        addDebug(`Test error: ${res.data.error}`);
      }
      return { success: res.data.success && res.data.sent > 0, result: res.data };
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      addDebug(`Test failed: ${errMsg}`);
      console.error('Failed to send test notification:', err);
      return { success: false, result: { error: errMsg } };
    }
  }, [portal]);

  // Get debug info for display
  const getDebugInfo = useCallback(() => ({
    status,
    debugLog: debugInfo,
    vapidKeyLength: vapidKey?.length || 0,
  }), [status, debugInfo, vapidKey]);

  return {
    status,
    preferences,
    subscribe,
    unsubscribe,
    loadPreferences,
    updatePreferences,
    sendTest,
    debugInfo,
    getDebugInfo,
  };
}

export default usePushNotifications;
